import { useState, useCallback, useRef } from 'react';

/**
 * 批量操作类型
 * @typedef {'generateImage' | 'generateVideo' | 'addCharacter' | 'addScene' | 'addProp' | null} BatchActionType
 */

/**
 * 批量进度状态
 * @typedef {Object} BatchProgress
 * @property {number} current - 当前完成数量
 * @property {number} total - 总数量
 */

/**
 * 自定义 Hook: 管理批量操作功能
 * @returns {Object} 批量操作相关的状态和方法
 */
export const useBatchOperations = () => {
  // 当前批量操作类型
  const [batchAction, setBatchAction] = useState(null);
  
  // 批量进度
  const [batchProgress, setBatchProgress] = useState({
    current: 0,
    total: 0,
  });
  
  // 批量添加参考的目标位置
  const [batchTargetIndex, setBatchTargetIndex] = useState(null);
  
  // 用于标记是否取消批量操作
  const isCancelledRef = useRef(false);
  // 用于标记是否正在执行批量操作
  const isProcessingRef = useRef(false);

  /**
   * 开始批量操作
   * @param {BatchActionType} actionType - 批量操作类型
   * @param {number} [targetIndex] - 批量添加参考的目标位置
   */
  const startBatchAction = useCallback((actionType, targetIndex = null) => {
    setBatchAction(actionType);
    setBatchTargetIndex(targetIndex);
    setBatchProgress({ current: 0, total: 0 });
    isCancelledRef.current = false;
  }, []);

  /**
   * 取消批量操作
   */
  const cancelBatchAction = useCallback(() => {
    isCancelledRef.current = true;
    isProcessingRef.current = false;
  }, []);

  /**
   * 重置批量进度
   */
  const resetBatchProgress = useCallback(() => {
    setBatchProgress({ current: 0, total: 0 });
    isCancelledRef.current = false;
    isProcessingRef.current = false;
  }, []);

  /**
   * 完成批量操作，重置状态
   */
  const finishBatchAction = useCallback(() => {
    setBatchAction(null);
    setBatchTargetIndex(null);
    setBatchProgress({ current: 0, total: 0 });
    isCancelledRef.current = false;
    isProcessingRef.current = false;
  }, []);

  /**
   * 批量生成图片（全部并发提交，由后端任务池管理排队）
   * @param {number[]} selectedIndices - 选中的行索引数组
   * @param {Function} generateFunction - 生成函数，接收 (index) => Promise<void>
   * @returns {Promise<{ success: boolean, completed: number, errors: Error[] }>}
   */
  const batchGenerateImages = useCallback(async (selectedIndices, generateFunction) => {
    if (isProcessingRef.current) {
      return { success: false, completed: 0, errors: [new Error('已有批量操作正在进行')] };
    }

    isProcessingRef.current = true;
    isCancelledRef.current = false;
    const total = selectedIndices.length;
    setBatchProgress({ current: 0, total });

    const errors = [];
    let completed = 0;

    try {
      // 全部并发提交，不限制并发数，让后端任务池管理排队
      const promises = selectedIndices.map((index) => {
        if (isCancelledRef.current) return Promise.resolve({ status: 'cancelled' });
        return generateFunction(index)
          .then(() => {
            completed++;
            setBatchProgress({ current: completed, total });
            return { status: 'success' };
          })
          .catch((err) => {
            errors.push(err);
            completed++;
            setBatchProgress({ current: completed, total });
            return { status: 'error', error: err };
          });
      });

      await Promise.all(promises);

      return { success: errors.length === 0, completed, errors };
    } finally {
      isProcessingRef.current = false;
    }
  }, []);

  /**
   * 批量生成视频（全部并发提交，由后端任务池管理排队）
   * @param {number[]} selectedIndices - 选中的行索引数组
   * @param {Function} generateFunction - 生成函数，接收 (index) => Promise<void>
   * @returns {Promise<{ success: boolean, completed: number, errors: Error[] }>}
   */
  const batchGenerateVideos = useCallback(async (selectedIndices, generateFunction) => {
    if (isProcessingRef.current) {
      return { success: false, completed: 0, errors: [new Error('已有批量操作正在进行')] };
    }

    isProcessingRef.current = true;
    isCancelledRef.current = false;
    const total = selectedIndices.length;
    setBatchProgress({ current: 0, total });

    const errors = [];
    let completed = 0;

    try {
      // 全部并发提交，不限制并发数，让后端任务池管理排队
      const promises = selectedIndices.map((index) => {
        if (isCancelledRef.current) return Promise.resolve({ status: 'cancelled' });
        return generateFunction(index)
          .then(() => {
            completed++;
            setBatchProgress({ current: completed, total });
            return { status: 'success' };
          })
          .catch((err) => {
            errors.push(err);
            completed++;
            setBatchProgress({ current: completed, total });
            return { status: 'error', error: err };
          });
      });

      await Promise.all(promises);

      return { success: errors.length === 0, completed, errors };
    } finally {
      isProcessingRef.current = false;
    }
  }, []);

  /**
   * 批量添加角色
   * @param {Object} character - 角色对象
   * @param {number} startIndex - 起始行索引
   * @param {number} endIndex - 结束行索引（包含）
   * @param {Function} updateRowFunction - 更新行函数，接收 (index, character) => void
   * @returns {Promise<{ success: boolean, updated: number }>}
   */
  const batchAddCharacter = useCallback(async (character, startIndex, endIndex, updateRowFunction) => {
    if (isProcessingRef.current) {
      return { success: false, updated: 0 };
    }

    isProcessingRef.current = true;
    isCancelledRef.current = false;
    const total = endIndex - startIndex + 1;
    setBatchProgress({ current: 0, total });

    let updated = 0;

    try {
      for (let i = startIndex; i <= endIndex; i++) {
        if (isCancelledRef.current) {
          break;
        }

        updateRowFunction(i, character);
        updated++;
        setBatchProgress({ current: updated, total });
      }
    } finally {
      isProcessingRef.current = false;
    }

    return {
      success: !isCancelledRef.current,
      updated,
      cancelled: isCancelledRef.current,
    };
  }, []);

  /**
   * 批量添加场景
   * @param {Object} scene - 场景对象
   * @param {number} startIndex - 起始行索引
   * @param {number} endIndex - 结束行索引（包含）
   * @param {Function} updateRowFunction - 更新行函数，接收 (index, scene) => void
   * @returns {Promise<{ success: boolean, updated: number }>}
   */
  const batchAddScene = useCallback(async (scene, startIndex, endIndex, updateRowFunction) => {
    if (isProcessingRef.current) {
      return { success: false, updated: 0 };
    }

    isProcessingRef.current = true;
    isCancelledRef.current = false;
    const total = endIndex - startIndex + 1;
    setBatchProgress({ current: 0, total });

    let updated = 0;

    try {
      for (let i = startIndex; i <= endIndex; i++) {
        if (isCancelledRef.current) {
          break;
        }

        updateRowFunction(i, scene);
        updated++;
        setBatchProgress({ current: updated, total });
      }
    } finally {
      isProcessingRef.current = false;
    }

    return {
      success: !isCancelledRef.current,
      updated,
      cancelled: isCancelledRef.current,
    };
  }, []);

  /**
   * 批量添加道具
   * @param {Object} prop - 道具对象
   * @param {number} startIndex - 起始行索引
   * @param {number} endIndex - 结束行索引（包含）
   * @param {Function} updateRowFunction - 更新行函数，接收 (index, prop) => void
   * @returns {Promise<{ success: boolean, updated: number }>}
   */
  const batchAddProp = useCallback(async (prop, startIndex, endIndex, updateRowFunction) => {
    if (isProcessingRef.current) {
      return { success: false, updated: 0 };
    }

    isProcessingRef.current = true;
    isCancelledRef.current = false;
    const total = endIndex - startIndex + 1;
    setBatchProgress({ current: 0, total });

    let updated = 0;

    try {
      for (let i = startIndex; i <= endIndex; i++) {
        if (isCancelledRef.current) {
          break;
        }

        updateRowFunction(i, prop);
        updated++;
        setBatchProgress({ current: updated, total });
      }
    } finally {
      isProcessingRef.current = false;
    }

    return {
      success: !isCancelledRef.current,
      updated,
      cancelled: isCancelledRef.current,
    };
  }, []);

  /**
   * 批量更新分镜
   * @param {Array} newShots - 新的分镜数组
   * @param {Array} currentRows - 当前行数据
   * @param {Object} options - 配置选项
   * @param {boolean} [options.merge=true] - 是否合并，true则合并，false则替换
   * @param {Function} [options.mergeFn] - 自定义合并函数
   * @returns {Array} 更新后的行数据
   */
  const handleBatchUpdateShots = useCallback((newShots, currentRows, options = {}) => {
    const { merge = true, mergeFn } = options;

    if (!newShots || !Array.isArray(newShots)) {
      return currentRows;
    }

    if (!merge) {
      // 直接替换模式
      return newShots.map((shot, index) => ({
        ...shot,
        id: shot.id || `shot-${Date.now()}-${index}`,
      }));
    }

    // 合并模式
    if (typeof mergeFn === 'function') {
      return mergeFn(currentRows, newShots);
    }

    // 默认合并策略：保留现有行，追加新行
    const existingIds = new Set(currentRows.map(row => row.id));
    const shotsToAdd = newShots
      .filter(shot => !existingIds.has(shot.id))
      .map((shot, index) => ({
        ...shot,
        id: shot.id || `shot-${Date.now()}-${index}`,
      }));

    return [...currentRows, ...shotsToAdd];
  }, []);

  /**
   * 检查是否正在处理批量操作
   * @returns {boolean}
   */
  const isProcessing = useCallback(() => {
    return isProcessingRef.current;
  }, []);

  /**
   * 检查是否已取消
   * @returns {boolean}
   */
  const isCancelled = useCallback(() => {
    return isCancelledRef.current;
  }, []);

  return {
    // 状态
    batchAction,
    batchProgress,
    batchTargetIndex,
    
    // 控制方法
    startBatchAction,
    cancelBatchAction,
    resetBatchProgress,
    finishBatchAction,
    
    // 批量操作方法
    batchGenerateImages,
    batchGenerateVideos,
    batchAddCharacter,
    batchAddScene,
    batchAddProp,
    handleBatchUpdateShots,
    
    // 状态检查
    isProcessing,
    isCancelled,
  };
};

export default useBatchOperations;
